/*
REGISTER FLOW:
1. Your details — fill form, accept T&Cs, send verification code
2. Verify email — enter 6-digit code, create account
3. All set — brief confirmation, redirect home
*/

import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthTextField from "../../components/Form/AuthTextField";
import AuthPasswordField from "../../components/Form/AuthPasswordField";
import PasswordRequirements from "../../components/Form/PasswordRequirements";
import SignupStepIndicator, {
  type SignupStep,
} from "../../components/Form/SignupStepIndicator";
import {
  registerUserAPI,
  sendEmailCodeAPI,
  verifyEmailCodeAPI,
} from "../../api/auth";
import { GlobalContext } from "../../context/globalContext";
import Spinner from "../../components/Spinner/Spinner";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { validatePassword } from "../../utils/passwordPolicy";

const termsPDF = require("../../assets/pdf/Events House Terms & Conditions.pdf");
const privacyPDF = require("../../assets/pdf/Events House Privacy Policy.pdf");

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";
const secondaryBtnClass =
  "eh-btn-secondary block w-full py-3 text-sm font-medium text-center mt-3";

export function Register() {
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(GlobalContext);

  const [step, setStep] = useState<SignupStep>("details");
  const [formInfo, setFormInfo] = useState({
    email: "",
    password: "",
    lastName: "",
    firstName: "",
    title: "",
    organisation: "",
    position: "",
    country: "",
    passwordRepeat: "",
    checkboxTermsPrivacy: false,
  });
  const [code, setCode] = useState("");
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");

  function validateForm(): string | null {
    const {
      email,
      password,
      lastName,
      firstName,
      title,
      organisation,
      position,
      country,
      passwordRepeat,
      checkboxTermsPrivacy,
    } = formInfo;
    if (
      !email ||
      !password ||
      !title ||
      !firstName ||
      !lastName ||
      !organisation ||
      !position ||
      !country ||
      !passwordRepeat
    ) {
      return "Please complete all required fields.";
    }
    if (!email.includes("@")) {
      return "Please enter a valid email address.";
    }
    const passwordErr = validatePassword(password);
    if (passwordErr) return passwordErr;
    if (password !== passwordRepeat) {
      return "Passwords do not match.";
    }
    if (!checkboxTermsPrivacy) {
      return "Please agree to the terms and conditions and privacy policy.";
    }
    return null;
  }

  async function handleSendCode(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setErrorMessage("");
    setMessage("");

    const err = validateForm();
    if (err) {
      setErrorMessage(err);
      return;
    }

    setIsSendingCode(true);
    try {
      const result = await sendEmailCodeAPI({ email: formInfo.email.trim() });
      if (result.ok) {
        setStep("verify");
        setMessage(
          "We sent a 6-digit code to your email. It expires in 10 minutes.",
        );
      } else {
        setErrorMessage(result.message ?? "Failed to send verification code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  }

  async function handleVerifyAndRegister(
    e: React.MouseEvent<HTMLButtonElement>,
  ) {
    e.preventDefault();
    setErrorMessage("");
    setMessage("");

    if (!/^\d{6}$/.test(code)) {
      setErrorMessage("Please enter the 6-digit code from your email.");
      return;
    }

    setIsSigningUp(true);
    try {
      const verify = await verifyEmailCodeAPI({
        email: formInfo.email.trim(),
        code,
      });
      if (!verify.ok) {
        setErrorMessage(verify.message ?? "Code verification failed.");
        setIsSigningUp(false);
        return;
      }

      const result = await registerUserAPI({
        email: formInfo.email.trim(),
        password: formInfo.password,
        title: formInfo.title.trim(),
        firstName: formInfo.firstName.trim(),
        lastName: formInfo.lastName.trim(),
        organisation: formInfo.organisation.trim(),
        position: formInfo.position.trim(),
        country: formInfo.country.trim(),
      });

      if (result.ok && result.user) {
        setStep("complete");
        setMessage("Your account has been created successfully.");
        setCurrentUser(result.user);
        setTimeout(() => {
          resetForm();
          navigate("/");
        }, 1500);
      } else {
        setErrorMessage(result.message ?? "Registration failed.");
      }
    } catch (error) {
      console.error("User creation error:", error);
      setErrorMessage(
        "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSigningUp(false);
    }
  }

  async function handleResendCode() {
    setErrorMessage("");
    setMessage("");
    setIsSendingCode(true);
    try {
      const result = await sendEmailCodeAPI({ email: formInfo.email.trim() });
      if (result.ok) {
        setMessage("A new code has been sent to your email.");
      } else {
        setErrorMessage(result.message ?? "Failed to resend code.");
      }
    } finally {
      setIsSendingCode(false);
    }
  }

  function resetForm() {
    setFormInfo({
      email: "",
      password: "",
      lastName: "",
      firstName: "",
      title: "",
      organisation: "",
      position: "",
      country: "",
      passwordRepeat: "",
      checkboxTermsPrivacy: false,
    });
    setCode("");
    setStep("details");
    setMessage("");
    setErrorMessage("");
  }

  return (
    <div>
      <SignupStepIndicator currentStep={step} />

      {step === "complete" && (
        <div className="text-center py-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-bold mb-4">
            ✓
          </div>
          <p className="font-semibold text-slate-900 mb-2">Welcome to Events House</p>
          <p className="text-sm text-slate-600">{message}</p>
          <p className="text-sm text-slate-500 mt-2">Redirecting you home…</p>
        </div>
      )}

      {step === "verify" && (
        <div className="flex flex-col gap-4">
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700">
            <p className="font-medium text-slate-900 mb-1">Check your inbox</p>
            <p>
              Enter the 6-digit code we sent to{" "}
              <strong>{formInfo.email}</strong>.
            </p>
          </div>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Verification code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="w-full text-center text-2xl tracking-[0.4em] border border-slate-300 bg-white px-3 py-3 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
            />
          </label>
          <button
            type="button"
            onClick={handleVerifyAndRegister}
            disabled={isSigningUp}
            className={primaryBtnClass}
          >
            {isSigningUp ? <Spinner /> : "Create account"}
          </button>
          <div className="flex flex-col sm:flex-row gap-3 justify-center text-sm">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isSendingCode}
              className="text-slate-700 underline hover:text-slate-900 font-medium"
            >
              {isSendingCode ? "Sending…" : "Resend code"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("details");
                setErrorMessage("");
              }}
              className="text-slate-700 underline hover:text-slate-900 font-medium"
            >
              Back to your details
            </button>
          </div>
          {errorMessage && <ErrorAlert>{errorMessage}</ErrorAlert>}
          {message && <p className="text-green-700 text-sm">{message}</p>}
        </div>
      )}

      {step === "details" && (
        <form
          id="form-register"
          onChange={() => {
            setMessage("");
            setErrorMessage("");
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
            <AuthTextField
              label="Title *"
              value={formInfo.title}
              onChange={(v) => setFormInfo({ ...formInfo, title: v })}
              hint="e.g. Dr, Prof, Mr"
              required
            />
            <AuthTextField
              label="First name *"
              value={formInfo.firstName}
              onChange={(v) => setFormInfo({ ...formInfo, firstName: v })}
              autoComplete="given-name"
              hint="Cannot be changed after registration"
              required
            />
            <AuthTextField
              label="Last name *"
              value={formInfo.lastName}
              onChange={(v) => setFormInfo({ ...formInfo, lastName: v })}
              autoComplete="family-name"
              hint="Cannot be changed after registration"
              required
            />
          </div>

          <AuthTextField
            label="Email *"
            type="email"
            value={formInfo.email}
            onChange={(v) => setFormInfo({ ...formInfo, email: v })}
            autoComplete="email"
            hint="Used for sign-in and order confirmations. Cannot be changed after registration."
            required
          />

          <AuthPasswordField
            label="Password *"
            value={formInfo.password}
            onChange={(v) => setFormInfo({ ...formInfo, password: v })}
            autoComplete="new-password"
            required
          />
          <PasswordRequirements password={formInfo.password} />
          <AuthPasswordField
            label="Confirm password *"
            value={formInfo.passwordRepeat}
            onChange={(v) => setFormInfo({ ...formInfo, passwordRepeat: v })}
            autoComplete="new-password"
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
            <AuthTextField
              label="Organisation *"
              value={formInfo.organisation}
              onChange={(v) => setFormInfo({ ...formInfo, organisation: v })}
              autoComplete="organization"
              required
            />
            <AuthTextField
              label="Position *"
              value={formInfo.position}
              onChange={(v) => setFormInfo({ ...formInfo, position: v })}
              required
            />
            <AuthTextField
              label="Country *"
              value={formInfo.country}
              onChange={(v) => setFormInfo({ ...formInfo, country: v })}
              autoComplete="country-name"
              required
            />
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700 mt-6 mb-6 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={formInfo.checkboxTermsPrivacy}
              onChange={() =>
                setFormInfo({
                  ...formInfo,
                  checkboxTermsPrivacy: !formInfo.checkboxTermsPrivacy,
                })
              }
            />
            <span>
              I agree to the{" "}
              <a
                className="underline text-slate-900"
                href={termsPDF}
                target="_blank"
                rel="noreferrer"
              >
                terms and conditions
              </a>{" "}
              and{" "}
              <a
                className="underline text-slate-900"
                href={privacyPDF}
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>
              .
            </span>
          </label>

          <button
            id="register-button"
            type="button"
            onClick={handleSendCode}
            className={primaryBtnClass}
            disabled={isSendingCode}
          >
            {isSendingCode ? "Sending code…" : "Continue — send verification code"}
          </button>
          <button type="button" onClick={resetForm} className={secondaryBtnClass}>
            Clear form
          </button>

          {errorMessage && (
            <ErrorAlert className="mt-4">{errorMessage}</ErrorAlert>
          )}
          {message && <p className="text-green-700 text-sm mt-4">{message}</p>}
        </form>
      )}
    </div>
  );
}
